import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import css from "./Tooltip.module.css";

const TooltipProvider = ({
  delay = 600,
  closeDelay = 0,
  timeout = 400,
  children,
}) => (
  <BaseTooltip.Provider delay={delay} closeDelay={closeDelay} timeout={timeout}>
    {children}
  </BaseTooltip.Provider>
);

TooltipProvider.displayName = "TooltipProvider";

TooltipProvider.propTypes = {
  delay: PropTypes.number,
  closeDelay: PropTypes.number,
  timeout: PropTypes.number,
  children: PropTypes.node,
};

const TooltipRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  trackCursorAxis = "none",
  children,
  ...rest
}) => (
  <BaseTooltip.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    onOpenChangeComplete={onOpenChangeComplete}
    trackCursorAxis={trackCursorAxis}
    {...rest}
  >
    {children}
  </BaseTooltip.Root>
);

TooltipRoot.displayName = "TooltipRoot";

TooltipRoot.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onOpenChangeComplete: PropTypes.func,
  trackCursorAxis: PropTypes.oneOf(["none", "x", "y", "both"]),
  children: PropTypes.node,
};

const TooltipTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseTooltip.Trigger ref={ref} className={className} {...rest}>
      {children}
    </BaseTooltip.Trigger>
  ),
);

TooltipTrigger.displayName = "TooltipTrigger";

TooltipTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const TooltipPopup = React.forwardRef(
  ({ side = "top", sideOffset = 8, className, children, ...rest }, ref) => (
    <BaseTooltip.Portal>
      <BaseTooltip.Positioner
        side={side}
        sideOffset={sideOffset}
        className={css.positioner}
      >
        <BaseTooltip.Popup
          ref={ref}
          className={clsx(css.popup, className)}
          {...rest}
        >
          <BaseTooltip.Arrow className={css.arrow} />
          {children}
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  ),
);

TooltipPopup.displayName = "TooltipPopup";

TooltipPopup.propTypes = {
  side: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  sideOffset: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
};

const Tooltip = {
  Provider: TooltipProvider,
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Popup: TooltipPopup,
};

export default Tooltip;
